import { useEffect, useState } from "react";

import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "@mui/material";
import { PageContainer } from "@/features/ui";

import { SalesPaymentForm } from "@/features/sales-payments";
import { apiOutletClient, getErrorMessage } from "@/features/shared";
import { CenteredLoading, useSnackbar } from "@/features/ui";

export const EditSalesPayment = () => {
  const { referenceNumber } = useParams();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(true);
  const [defaultValues, setDefaultValues] = useState(null);

  useEffect(() => {
    const fetchSalesPayment = async () => {
      setIsLoading(true);
      try {
        const {
          data: { data },
        } = await apiOutletClient.get(`sales-payments/${referenceNumber}`, {
          params: {
            withBuyer: true,
            withSeller: true,
            withCashBankOutletChartOfAccount: true,
            withSalesInvoicePayments: true,
          },
        });

        setDefaultValues({
          transactionDate: dayjs(data.transactionDate),
          seller: data.seller,
          buyer: data.buyer,
          outletChartOfAccount: data.cashBankOutletChartOfAccount ?? null,
          note: data.note,
          salesInvoicePayments: data.salesInvoicePayments.map((payment) => ({
            id: payment.id,
            salesInvoiceId: payment.salesInvoiceId,
            referenceNumber: payment.salesInvoice.referenceNumber,
            buyerReferenceNumber: payment.salesInvoice.buyerReferenceNumber,
            transactionDate: payment.salesInvoice.transactionDate,
            grandTotal: payment.salesInvoice.grandTotal,
            outstandingAmount: payment.outstandingAmount,
            allocatedAmount: payment.allocatedAmount,
            discount: payment.discount,
            discountAccounts: (payment.discountAccounts ?? []).map(
              (discountAccount) => ({
                id: discountAccount.id,
                outletChartOfAccount: discountAccount.outletChartOfAccount,
                amount: discountAccount.amount,
                // The note isn't persisted on the discount row (it only lives on
                // the journal voucher line), so it can't be rehydrated on edit.
                note: "",
              })
            ),
          })),
        });
      } catch (error) {
        showSnackbar(
          getErrorMessage(error, "Gagal memperbarui pembayaran penjualan")
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchSalesPayment();
  }, [referenceNumber, showSnackbar]);

  const handleSubmit = async (formData) => {
    try {
      // Strict header-only replace: the endpoint never touches allocations,
      // discounts or the grand total. The form already builds exactly this
      // shape in edit mode; the outlet travels in the header.
      const { sellerId, ...payload } = formData;

      await apiOutletClient.put(
        `sales-payments/${referenceNumber}`,
        payload,
        {
          headers: {
            "Outlet-Id": sellerId,
          },
        }
      );
      navigate(-1);
    } catch (error) {
      showSnackbar(
        getErrorMessage(error, "Gagal memperbarui pembayaran penjualan")
      );
    }
  };

  if (!defaultValues && isLoading) {
    return <CenteredLoading />;
  }

  return (
    <PageContainer
      maxWidth="xl"
      title="Ubah Penerimaan Penjualan"
      breadcrumbs={[
        { title: "Penerimaan Penjualan", path: "/sales/sales-payments" },
        {
          title: referenceNumber,
          path: `/sales/sales-payments/${referenceNumber}`,
        },
        { title: "Ubah" },
      ]}
    >
      <SalesPaymentForm
        editMode={true}
        isLoading={isLoading}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        renderActionButton={({ isSubmitting }) => (
          <Button type="submit" variant="contained" loading={isSubmitting}>
            Simpan
          </Button>
        )}
      />
    </PageContainer>
  );
};
